import { get, head } from 'lodash-es';
import { getVehicleDescription } from 'site-modules/shared/utils/inventory-utils/get-vehicle-description';
import { transformVehicleHistory } from 'site-modules/shared/utils/inventory/srp-card-utils';
import { ALLOWED_HISTORY_PROVIDERS } from 'site-modules/shared/constants/inventory/vehicle-history';
import { showTrimName } from 'client/site-modules/shared/utils/show-trim-name';
import { getUsurpImages } from 'client/site-modules/shared/utils/inventory/srp-images';

import { getOfferSchema } from './offer';

export function getVehicleEngine(vehicle) {
  const partsInfo = get(vehicle, 'vehicleInfo.partsInfo', {});
  const { cylinders, engineType, fuelType, enginePower, engineSize } = partsInfo;
  const hasEngineData = Object.values({ cylinders, engineType, fuelType, enginePower, engineSize }).some(Boolean);
  const engineSpecification = { '@type': 'EngineSpecification' };
  if (cylinders) {
    engineSpecification.name = `${cylinders} Cyl`;
  }
  if (engineType) {
    engineSpecification.engineType = engineType;
  }
  if (fuelType) {
    engineSpecification.fuelType = fuelType;
  }
  if (enginePower) {
    engineSpecification.enginePower = {
      '@type': 'QuantitativeValue',
      value: enginePower,
      unitText: 'horsepower',
    };
  }
  if (engineSize) {
    engineSpecification.engineDisplacement = {
      '@type': 'QuantitativeValue',
      value: engineSize,
      unitCode: 'LTR',
    };
  }
  return hasEngineData ? engineSpecification : undefined;
}

export function getVehicleVinSchema({ vehicle, disableInventoryMLPhotoSort, isSeot4290, city, stateName, urlPattern }) {
  const { stockNumber, vin } = vehicle;
  const { make, model, year, style, trim = '' } = get(vehicle, 'vehicleInfo.styleInfo', {});
  const { transmission, driveTrain } = get(vehicle, 'vehicleInfo.partsInfo', {});
  const mileage = get(vehicle, 'vehicleInfo.mileage', '');
  const epaCombinedMPG = get(vehicle, 'vehicleInfo.styleInfo.fuel.epaCombinedMPG');
  const numberOfSeats = get(vehicle, 'vehicleInfo.styleInfo.numberOfSeats');
  const historyInfo = get(vehicle, 'historyInfo', {});
  const { ownerText, usageType, historyProvider } = historyInfo;
  const { accidentText } = transformVehicleHistory(historyInfo);
  const vehicleEngine = getVehicleEngine(vehicle);
  const { photoUrls } = getUsurpImages(vehicle, disableInventoryMLPhotoSort);
  const image = head(photoUrls);

  return {
    '@type': isSeot4290 ? 'Car' : 'Vehicle',
    name: `${year} ${make} ${model} ${style} ${showTrimName(make, trim) ? trim : ''}`,
    description: getVehicleDescription(vehicle),
    brand: {
      '@type': 'Brand',
      name: make,
    },
    ...(isSeot4290 && { model }),
    vehicleModelDate: year,
    bodyType: get(vehicle, 'vehicleInfo.styleInfo.bodyType'),
    image,
    color: get(vehicle, 'vehicleInfo.vehicleColors.exterior.name'),
    sku: stockNumber,
    driveWheelConfiguration: driveTrain,
    vehicleConfiguration: style,
    vehicleTransmission: transmission,
    vehicleIdentificationNumber: vin,
    mileageFromOdometer: mileage
      ? {
          '@type': 'QuantitativeValue',
          value: mileage,
          unitCode: 'SMI',
        }
      : undefined,
    ...(epaCombinedMPG && {
      fuelEfficiency: {
        '@type': 'QuantitativeValue',
        unitCode: 'MPG',
        value: epaCombinedMPG,
      },
    }),
    ...(numberOfSeats && { seatingCapacity: numberOfSeats }),
    ...(vehicleEngine && { vehicleEngine }),
    offers: getOfferSchema({ vehicle, urlCity: city, urlStateName: stateName, urlPattern }),
    ...(ALLOWED_HISTORY_PROVIDERS.includes(historyProvider) && {
      knownVehicleDamages: accidentText,
      numberOfPreviousOwners: ownerText,
      vehicleSpecialUsage: usageType,
    }),
  };
}
